import { isResultSuccess } from '../types';
/**
 * Menu service
 */
class MenuService {
    menuAdapter;
    sollicitationsService;
    sessionService;
    publicSettingsService;
    tiersService;
    storeService;
    contributionsVEFService;
    constructor({ menuAdapter, sollicitationsService, sessionService, publicSettingsService, tiersService, storeService, contributionsVEFService, }) {
        this.menuAdapter = menuAdapter;
        this.sollicitationsService = sollicitationsService;
        this.sessionService = sessionService;
        this.publicSettingsService = publicSettingsService;
        this.tiersService = tiersService;
        this.storeService = storeService;
        this.contributionsVEFService = contributionsVEFService;
    }
    static #MODULES_NAMES = {
        stages: 'offresStageActif',
        echanges: 'echangesActif',
    };
    dashboardEntry = {
        id: 'dashboard',
        route: 'app.connected.dashboard.accueil',
        icon: 'home-outline',
    };
    sollicitationsEntry = {
        id: 'sollicitations',
        route: 'app.connected.dashboard.sollicitations',
        icon: 'pen-fancy-outline',
    };
    demandesEntry = {
        id: 'aides',
        icon: 'folder-outline',
        route: 'app.connected.dashboard.aides.demandesAides',
        subMenu: [
            { id: 'access', route: 'app.connected.dashboard.aides.demandesAides' },
            { id: 'depot', route: 'app.connected.dashboard.deposerDemandeAide' },
        ],
    };
    stagesEntry = {
        id: 'stages',
        icon: 'briefcase-outline',
        subMenu: [
            { id: 'access', route: 'app.connected.dashboard.offresStage.mesOffres' },
            { id: 'depot', route: 'app.connected.config.depot.offresStage', params: { configurationId: 'OFFRESTAGE' } },
        ],
    };
    documentsEntry = {
        id: 'documents',
        route: 'app.connected.dashboard.documentsReçus',
        icon: 'file-text-outline',
    };
    echangesEntry = {
        id: 'echanges',
        route: 'app.connected.dashboard.echanges.centreEchanges',
        icon: 'conversation-outline',
    };
    demandesApprenantsEntry = {
        id: 'demandes-apprenants',
        icon: 'graduation-cap-outline',
        subMenu: [
            { id: 'access', route: 'app.connected.dashboard.demandesApprenants' },
            { id: 'contributions', route: 'app.connected.dashboard.entreesFormation' },
        ],
    };
    displayDemandesApprenantsEntry = false;
    isTiersDefined = false;
    isEchangesModuleActivated = false;
    /**
     * Get menu
     */
    async getMenu() {
        const menu = [];
        await this.publicSettingsService.loadPublicSettings();
        const publicSettings = this.storeService.getPublicSettings().data();
        menu.push({ ...this.dashboardEntry });
        this.displayDemandesApprenantsEntry = await this.checkDisplayDemandesApprenantsEntry();
        if (this.displayDemandesApprenantsEntry) {
            menu.push({ ...this.demandesApprenantsEntry });
        }
        menu.push({ ...this.sollicitationsEntry });
        const demandesEntry = { ...this.demandesEntry };
        if (publicSettings?.createDemande)
            delete demandesEntry.route; // Show menu with sub-entries
        else
            delete demandesEntry.subMenu; // Show menu without sub-entries
        menu.push({ ...demandesEntry });
        // check if offre-stage module is activated
        const isStageModuleActivated = await this.checkIsStageModuleActivated();
        if (isStageModuleActivated) {
            menu.push({ ...this.stagesEntry });
        }
        // check if tiers is defined
        this.isTiersDefined = Boolean(await this.sessionService.getTiersReference());
        if (this.isTiersDefined) {
            menu.push({ ...this.documentsEntry });
        }
        // check if echanges module is activated
        this.isEchangesModuleActivated = await this.checkIsEchangesModuleActivated();
        if (this.isEchangesModuleActivated) {
            menu.push({ ...this.echangesEntry });
        }
        return { response: menu };
    }
    /**
     * Get menu count
     * getMenu function needs to be called before to set entries visibility
     */
    async getMenuCount() {
        const menuCount = {};
        if (this.displayDemandesApprenantsEntry) {
            const contributionsApprenantsCount = await this.getContributionsApprenantsCount();
            if (isResultSuccess(contributionsApprenantsCount)) {
                menuCount.contributionsDemandesApprenants = undefined; // Set entry to undefined to remove count if needed
                if (!this.#isResponseEmpty(contributionsApprenantsCount.response)) {
                    menuCount.contributionsDemandesApprenants = contributionsApprenantsCount.response;
                }
            }
        }
        const sollicitationsCount = await this.getSollicitationsCount();
        if (isResultSuccess(sollicitationsCount)) {
            menuCount.sollicitations = undefined; // Set entry to undefined to remove count if needed
            if (!this.#isResponseEmpty(sollicitationsCount.response)) {
                menuCount.sollicitations = sollicitationsCount.response;
            }
        }
        if (this.isTiersDefined) {
            const documentsCount = await this.getDocumentsCount();
            if (isResultSuccess(documentsCount)) {
                menuCount.documents = undefined; // Set entry to undefined to remove count if needed
                if (!this.#isResponseEmpty(documentsCount.response)) {
                    menuCount.documents = documentsCount.response;
                }
            }
        }
        if (this.isEchangesModuleActivated) {
            const echangesCount = await this.getEchangesCount();
            if (isResultSuccess(echangesCount)) {
                menuCount.echanges = undefined; // Set entry to undefined to remove count if needed
                if (![0, '0'].includes(echangesCount.response)) {
                    menuCount.echanges = echangesCount.response;
                }
            }
        }
        return { response: menuCount };
    }
    /**
     * Get documents counts
     */
    async getDocumentsCount() {
        return await this.menuAdapter.getDocumentsCount();
    }
    /**
     * Get sollicitations count
     */
    async getSollicitationsCount() {
        return await this.sollicitationsService.countTotal();
    }
    /**
     * Get echanges count
     */
    async getEchangesCount() {
        return await this.menuAdapter.getEchangesCount();
    }
    /**
     * Check if stage module is activated
     */
    checkIsStageModuleActivated() {
        return this.sessionService.isModuleActivated(MenuService.#MODULES_NAMES.stages);
    }
    /**
     * Check if echanges module is activated
     */
    checkIsEchangesModuleActivated() {
        return this.sessionService.isModuleActivated(MenuService.#MODULES_NAMES.echanges);
    }
    /**
     * Check if demandes-apprenants menu entry should be displayed
     */
    async checkDisplayDemandesApprenantsEntry() {
        const result = await this.tiersService.canCurrentTiersManageApprenants();
        return result ?? false;
    }
    /**
     * Get open contributions VEF
     */
    async getContributionsApprenantsCount() {
        const totalContributionsVEF = await this.contributionsVEFService.getContributionsVEFCount();
        const formattedTotal = totalContributionsVEF > 99 ? '99+' : `${totalContributionsVEF || '0'}`;
        return { response: formattedTotal };
    }
    #isResponseEmpty(response) {
        return [0, '0'].includes(response);
    }
}
export { MenuService };
//# sourceMappingURL=menu-service.js.map